import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import type { ReactElement } from 'react'
import React from 'react'

import { Button, Card, Carousel } from '@s4l/ds'
import { ListOfProperty } from '@s4l/stayforlong/property/domain/ListOfProperty'
import { IconArrowLeft } from '@s4l/icons/IconArrowLeft'
import { IconArrowRight } from '@s4l/icons/IconArrowRight'
import { PropertyCard } from '../../index'
import type { Property } from '@s4l/stayforlong/property/domain/Property'

const DEFAULT_ITEMS_PER_PAGE: [number, number, number] = [1.25, 3, 4]

interface IPropertiesCarouselProps {
  propertiesJSON: ReturnType<ListOfProperty['toJSON']>
  children?: (property: Property, index: number) => ReactElement
  itemsPerPage?: [number, number, number]
  checkInOffset?: number
  onPropertyClick?: (propertyId: string, position?: number) => void
}

interface IPlaceholderProps {
  itemsPerPage?: [number, number, number]
}

function PropertiesCarousel(props: IPropertiesCarouselProps) {
  const {
    propertiesJSON,
    children,
    itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
    checkInOffset = 0,
    onPropertyClick,
  } = props

  const properties = ListOfProperty.fromJSONOrEmpty({ json: propertiesJSON })

  if (properties.isEmpty()) return null

  return (
    <Carousel preset="multiple-items">
      <Carousel.Content itemsPerPage={itemsPerPage}>
        {properties.map((property, index) => {
          const customChildren = children?.(property, index)

          return (
            <PropertyCard
              key={property.id}
              variant="fixed-height"
              propertyJSON={property.toJSON()}
              checkInOffset={checkInOffset}
              onClick={() => onPropertyClick?.(property.id, index + 1)}
            >
              {React.isValidElement(customChildren) && customChildren.type === React.Fragment
                ? (customChildren.props as { children: React.ReactNode }).children
                : customChildren}
            </PropertyCard>
          )
        })}
      </Carousel.Content>
      <Carousel.Buttons>
        <Button variant="icon-rounded" size="small" leadingIcon={<IconArrowLeft />} />
        <Button variant="icon-rounded" size="small" leadingIcon={<IconArrowRight />} />
      </Carousel.Buttons>
    </Carousel>
  )
}

function Placeholder({ itemsPerPage = DEFAULT_ITEMS_PER_PAGE }: IPlaceholderProps) {
  return (
    <Carousel preset="multiple-items">
      <Carousel.Content itemsPerPage={itemsPerPage}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Card.Placeholder key={index} variant="fixed-height" /> //eslint-disable-line
        ))}
      </Carousel.Content>
      <Carousel.Buttons>
        <Button variant="icon-rounded" size="small" leadingIcon={<IconArrowLeft />} disabled />
        <Button variant="icon-rounded" size="small" leadingIcon={<IconArrowRight />} disabled />
      </Carousel.Buttons>
    </Carousel>
  )
}
Placeholder.displayName = 'Placeholder'

Placeholder.displayName = 'PropertiesCarousel.Placeholder'
PropertiesCarousel.Placeholder = withErrorBoundary(Placeholder)

PropertiesCarousel.displayName = 'PropertiesCarousel'
export default withErrorBoundary(PropertiesCarousel)
